import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../lib/api';

const TOKEN_KEY = 'vb_admin_token';
const AuthContext = createContext(null);

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const formatApiError = (err, fallback = 'Something went wrong. Please try again.') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join(' ');
  return detail.msg || String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); return; }
    adminApi.get('/auth/me').then(res => setUser(res.data)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    });
  }, []);

  useEffect(() => {
    const id = adminApi.interceptors.response.use(r => r, (err) => {
      if (err.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      }
      return Promise.reject(err);
    });
    return () => adminApi.interceptors.response.eject(id);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await adminApi.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    setUser(res.data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="admin-auth-checking">Checking session...</div>;
  }
  if (!user) return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  return children;
};
